// src/pages/SensaphonePage.jsx
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PageBanner from "../components/common/PageBanner";
import MediaModal from "../components/support/MediaModal";
import { useLocale } from "../contexts/LocaleContext";
import { getAllProductData } from "../api/apiService";
import DownloadsSection from "../components/products/DownloadsSection";
import SupportSection from "../components/products/SupportSection";
import SensaphoneOverview from "../components/products/sensaphone/SensaphoneOverview";
import SensaphoneIntroduction from "../components/products/sensaphone/SensaphoneIntroduction";
import SensaphoneFeatures from "../components/products/sensaphone/SensaphoneFeatures";
import SensaphoneSpecification from "../components/products/sensaphone/SensaphoneSpecification";
import SensaphoneSpecialCases from "../components/products/sensaphone/SensaphoneSpecialCases";
import SensaphoneAccessoriesPage from "./SensaphoneAccessoriesPage";
import BackToProductButton from "../components/products/BackToProductButton";
import ProductThemeShell from "../components/products/ProductThemeShell";

const SensaphonePage = () => {
  const { locale } = useLocale();
  const location = useLocation();
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sensaphoneItem, setSensaphoneItem] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [accessoriesCategory, setAccessoriesCategory] = useState(null);

  // Safely render text fields that might come from rich text / object structures
  const safeText = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (Array.isArray(value)) {
      return value.map((v) => safeText(v)).join(" ");
    }
    if (typeof value === "object") {
      if (Array.isArray(value.children)) {
        return value.children.map((child) => safeText(child)).join(" ");
      }
      if (typeof value.text === "string") return value.text;
    }
    return "";
  };

  useEffect(() => {
    const fetchProductContent = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getAllProductData(locale);
        setProductData(data);
      } catch (err) {
        setError(err.message || "Failed to load content.");
        setProductData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProductContent();
  }, [locale]);

  // Find the matching sensaphone item or accessories category by URL
  useEffect(() => {
    if (!productData) return;

    const pathname = location.pathname.toLowerCase();

    // 1. Try match accessories category first
    const accessoriesCategories =
      productData.cas_sensaphone_accessories_categories || [];

    const matchingAccessoriesCategory = accessoriesCategories.find((cat) => {
      const catUrl =
        typeof cat.url === "string" ? cat.url.toLowerCase() : "";
      return catUrl === pathname;
    });

    if (matchingAccessoriesCategory) {
      setAccessoriesCategory(matchingAccessoriesCategory);
      setSensaphoneItem(null);
      return;
    }

    // 2. Fallback to normal sensaphone item
    const sensaphoneItems = productData.cas_sensaphone_items || [];
    const matchingItem = sensaphoneItems.find((item) => {
      const itemUrl =
        typeof item.url === "string" ? item.url.toLowerCase() : "";
      return itemUrl === pathname;
    });

    setSensaphoneItem(matchingItem || null);
    setAccessoriesCategory(null);
  }, [productData, location.pathname]);

  const handleMediaClick = (media) => {
    setSelectedMedia(media);
  };

  const handleCloseModal = () => {
    setSelectedMedia(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
        <p className="text-xl text-gray-500">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-red-500 text-lg">{error}</p>
      </div>
    );
  }

  if (!sensaphoneItem && !accessoriesCategory) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-gray-700 text-xl">Content not available.</p>
        <BackToProductButton />
      </div>
    );
  }

  // Extract banner data from productData
  const banner = productData?.banner;
  const bannerTitle =
    safeText(sensaphoneItem?.name || accessoriesCategory?.name) ||
    "Sensaphone";
  const bannerImageUrl = banner?.image?.[0]?.url || banner?.url;
  const bannerTitleIsWhite = banner?.titleColorIsWhite ?? true;

  // Extract overview data (only for main sensaphone items)
  const overviewTitle = sensaphoneItem
    ? safeText(sensaphoneItem.overview_title)
    : "";
  const overviewData = sensaphoneItem?.overview || [];

  // Extract introduction data
  const introductionTitle = sensaphoneItem
    ? safeText(sensaphoneItem.introduction_title)
    : "";
  const introductionContent = sensaphoneItem?.introduction?.content;
  const introductionImage = sensaphoneItem?.introduction?.image;

  // Extract features data
  const featuresTitle = sensaphoneItem
    ? safeText(sensaphoneItem.features_title)
    : "";
  const featuresData = sensaphoneItem?.features;

  // Extract specification data
  const specificationTitle = sensaphoneItem
    ? safeText(sensaphoneItem.specification_title)
    : "";
  const specificationData = sensaphoneItem?.specification || [];

  const specialCaseRaw = sensaphoneItem?.cas_special_case;
  const specialCases = Array.isArray(specialCaseRaw)
    ? specialCaseRaw
    : specialCaseRaw
      ? [specialCaseRaw]
      : [];


  // Extract support data
  const supportTitle = sensaphoneItem
    ? safeText(sensaphoneItem.support_title)
    : "";
  const featuresImageList = sensaphoneItem?.features_image || [];
  const featuresVideoList = sensaphoneItem?.features_video || [];

  // Extract downloads data
  const downloadsTitle = sensaphoneItem
    ? safeText(sensaphoneItem.download_title)
    : "";
  const downloadsFiles = sensaphoneItem?.download || [];

  // If this is an accessories category URL, delegate rendering to SensaphoneAccessoriesPage
  if (accessoriesCategory) {
    return <SensaphoneAccessoriesPage banner={banner} category={accessoriesCategory} />;
  }

  return (
    <ProductThemeShell>
    <div className="sensaphone-page min-h-screen">
      {/* Banner */}
      <PageBanner
        title={bannerTitle}
        titleColorIsWhite={bannerTitleIsWhite}
        imageUrl={bannerImageUrl}
        bannerHeightClass="h-56 sm:h-64 md:h-80 lg:h-96"
      />

      {/* Main Content */}
      <section className="product-section-surface w-full py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Overview Section */}
        {overviewTitle && (
          <SensaphoneOverview
            title={overviewTitle}
            rows={overviewData}
            safeText={safeText}
          />
        )}

        {/* Introduction Section */}
        {!accessoriesCategory && introductionTitle && (
          <SensaphoneIntroduction
            title={introductionTitle}
            content={introductionContent}
            image={introductionImage}
            safeText={safeText}
          />
        )}

        {/* Features Section */}
        {!accessoriesCategory && featuresTitle && featuresData && featuresData[0] && (
          <SensaphoneFeatures
            title={featuresTitle}
            features={featuresData}
            safeText={safeText}
            onMediaClick={handleMediaClick}
          />
        )}

        {/* Specification Section */}
        {!accessoriesCategory && specificationTitle && (
          <SensaphoneSpecification
            title={specificationTitle}
            rows={specificationData}
            safeText={safeText}
          />
        )}

        {!accessoriesCategory && specialCases.length > 0 && (
          <SensaphoneSpecialCases cases={specialCases} safeText={safeText} />
        )}

        {/* Support Section */}
        {!accessoriesCategory &&
          supportTitle &&
          (featuresImageList.length > 0 ||
            featuresVideoList.length > 0) && (
            <SupportSection
              title={supportTitle}
              videos={featuresVideoList}
              photos={featuresImageList}
              safeText={safeText}
              onMediaClick={handleMediaClick}
            />
          )}

        {/* Downloads Section */}
        {!accessoriesCategory && downloadsTitle && (
          <DownloadsSection
            title={downloadsTitle}
            files={downloadsFiles}
            safeText={safeText}
          />
        )}

        <BackToProductButton />
        </div>
      </section>

      {/* Media Modal */}
      <MediaModal
        isOpen={!!selectedMedia}
        onClose={handleCloseModal}
        media={selectedMedia}
      />
    </div>
    </ProductThemeShell>
  );
};

export default SensaphonePage;

